import { Box, Grid } from '@chakra-ui/react';
import { Temporal } from 'temporal-polyfill';
import { ICalendarEventMinimumData } from '../../types/ICalendarEvent';
import { IEventMetadata } from '../../types/IEventMetadata';
import { ITimeZone } from '../../types/ITimeZone';
import { HourTick } from './components/hour-tick.view';
import WeekCalendarDayHighLight from './components/week-calendar-day-highlight/week-calendar-day-highlight.view';
import WeekCalendarEvent from './components/week-calendar-event/week-calendar-event.view';
import { useWeekCalendarDayController } from './week-calendar-day.controller';
import { calculatePositionAndHeight, ICalendarEventExtendedWithUIProps } from './week-calendar-day.service';

interface IWeekCalendarDayProps<TEvent extends ICalendarEventMinimumData> {
	/**
	 * Emitted when the event being updated (moved, start end changed) but the update is not finished.
	 */
	onEventPartialUpdated: (event: TEvent) => void;
	onEventUpdated: (event: TEvent) => void;
	onCalendarPointerDown: (start: Temporal.PlainDateTime) => void;
	onCalendarPointerMove: (start: Temporal.PlainDateTime) => void;
	onCalendarPointerUp: (start: Temporal.PlainDateTime) => void;
	onEventClick: (args: { event: React.MouseEvent; calendarEvent: TEvent }) => void;
	events: Array<TEvent>;
	hourHeightInPixels: number;
	plainDate: Temporal.PlainDate;
	timeZone: ITimeZone;
	highlight: null | { start: Temporal.ZonedDateTime; end: Temporal.ZonedDateTime };
	InnerEvent?: React.ElementType<{ event: TEvent; metadata: IEventMetadata<TEvent> }>;
}

const hours = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];

export function WeekCalendarDay<TEvent extends ICalendarEventMinimumData>(props: IWeekCalendarDayProps<TEvent>) {
	const {
		events,
		state,
		handleEventPointerDown,
		handleEventResizePointerDown,
		handlePointerDown,
		handlePointerMove,
		handlePointerUp,
		handleEventPointerUp,
		handleEventResizePointerUp,
	} = useWeekCalendarDayController(props);

	const highLight =
		props.highlight && props.highlight.start.toPlainDate().equals(props.plainDate)
			? calculatePositionAndHeight(
					{ id: '', ...props.highlight, dayContainMode: 'contain', isOverlapped: false, zIndex: 1, left: '0' },
					props.hourHeightInPixels,
					props.timeZone
			  )
			: null;

	// TODO: Add type for this
	const phantomEvent = state.phantomEvent
		? (state.phantomEvent as TEvent & {
				top: string;
				h: string;
				durationMinutes: number;
				left: string;
				zIndex: number;
				isOverlapped: boolean;
				dayContainMode: 'contain' | 'start' | 'end' | 'overflow';
		  })
		: null;

	const editedEvent = state.editedEvent
		? ({
				...state.editedEvent,
				...calculatePositionAndHeight(
					state.editedEvent as ICalendarEventExtendedWithUIProps<TEvent>,
					props.hourHeightInPixels,
					props.timeZone
				),
		  } as TEvent & {
				top: string;
				h: string;
				durationMinutes: number;
				left: string;
				zIndex: number;
				isOverlapped: boolean;
				dayContainMode: 'contain' | 'start' | 'end' | 'overflow';
		  })
		: null;

	return (
		<Box
			scrollSnapAlign="center"
			minW={{ base: 'full', lg: 'auto' }}
			onPointerDown={handlePointerDown}
			onPointerMove={handlePointerMove}
			onPointerUp={handlePointerUp}
			h={props.hourHeightInPixels * 24}
			borderRightColor="ui-element-divider"
			_first={{ borderRightColor: 'ui-element-divider', borderLeftWidth: 1 }}
			borderRightWidth={1}
			position="relative"
			scrollSnapType="x mandatory"
			scrollSnapStop="always"
			role="cell"
		>
			<Box aria-hidden>
				{hours.map((hour) => (
					<HourTick hourHeightInPixels={props.hourHeightInPixels} key={hour.toString()} />
				))}
			</Box>

			{highLight ? <WeekCalendarDayHighLight {...highLight} /> : null}

			<Box as="ol">
				{events
					.filter((e) => e.id !== state.editedEvent?.id)
					.map((event) => {
						return (
							<Grid
								onPointerDown={(e) => handleEventPointerDown(e, event)}
								onPointerUp={(e) => handleEventPointerUp(e, event)}
								px={0.5}
								as="li"
								css={{ touchAction: 'none' }}
								data-event="true"
								key={event.id}
								userSelect="none"
								position="absolute"
								left={event.left}
								right={0}
								top={event.top}
								h={event.h}
								transition="transform .3s"
								transformOrigin="center top"
								zIndex={state.editedEvent?.id === event.id ? 999 : event.zIndex}
								transform={state.editedEvent?.id === event.id ? 'scale(1.02)' : 'none'}
								shadow={state.editedEvent?.id === event.id ? 'sm' : 'none'}
								w={`calc(100% - ${event.left})`}
								cursor="move"
							>
								<Box
									css={{ touchAction: 'none' }}
									visibility={{
										base: state.editedEvent?.id === event.id ? 'visible' : 'hidden',
										md: 'visible',
									}}
									h={{ base: 6, md: 2 }}
									w={{ base: 6, md: 'full' }}
									onPointerDown={(e) => handleEventResizePointerDown(e, event, 'start')}
									onPointerUp={() => handleEventResizePointerUp()}
									cursor="row-resize"
									bg={{ base: 'gray.200', md: 'transparent' }}
									zIndex={4}
									position="absolute"
									top={{ base: -3, md: -0.5 }}
									right={{ base: 4, md: 0 }}
									borderRadius="full"
								/>
								{props.InnerEvent ? (
									<props.InnerEvent event={event} metadata={{ ...event }} />
								) : (
									<WeekCalendarEvent event={event} />
								)}
								<Box
									css={{ touchAction: 'none' }}
									position="absolute"
									visibility={{
										base: state.editedEvent?.id === event.id ? 'visible' : 'hidden',
										md: 'visible',
									}}
									h={{ base: 6, md: 2 }}
									w={{ base: 6, md: 'full' }}
									bottom={{ base: -3, md: -0.5 }}
									left={{ base: 4, md: 0 }}
									borderRadius="full"
									className="resize-handle"
									onPointerDown={(e) => handleEventResizePointerDown(e, event, 'end')}
									onPointerUp={() => handleEventResizePointerUp()}
									cursor="row-resize"
									bg={{
										base: 'gray.200',
										md: 'transparent',
									}}
									zIndex={4}
								></Box>
							</Grid>
						);
					})}

				{phantomEvent ? (
					<Grid
						opacity={0.3}
						px={0.5}
						as="li"
						css={{ touchAction: 'none' }}
						data-event="true"
						userSelect="none"
						position="absolute"
						left={phantomEvent.left}
						right={0}
						top={phantomEvent.top}
						h={phantomEvent.h}
						w={`calc(100% - ${phantomEvent.left})`}
						cursor="move"
					>
						{props.InnerEvent ? (
							<props.InnerEvent event={phantomEvent} metadata={{ ...phantomEvent }} />
						) : (
							<WeekCalendarEvent event={phantomEvent} />
						)}
					</Grid>
				) : null}

				{editedEvent ? (
					<Grid
						onPointerDown={(e) => handleEventPointerDown(e, editedEvent)}
						onPointerUp={(e) => handleEventPointerUp(e, editedEvent)}
						px={0.5}
						as="li"
						css={{ touchAction: 'none' }}
						data-event="true"
						userSelect="none"
						position="absolute"
						left={editedEvent.left}
						right={0}
						top={editedEvent.top}
						h={editedEvent.h}
						transition="transform .3s"
						transformOrigin="center top"
						zIndex={999}
						transform="scale(1.02)"
						shadow={'sm'}
						w={`calc(100% - ${editedEvent.left})`}
						cursor="move"
					>
						<Box
							css={{ touchAction: 'none' }}
							visibility={{
								base: 'visible',
								md: 'visible',
							}}
							h={{ base: 6, md: 2 }}
							w={{ base: 6, md: 'full' }}
							onPointerDown={(e) => handleEventResizePointerDown(e, editedEvent, 'start')}
							onPointerUp={() => handleEventResizePointerUp()}
							cursor="row-resize"
							bg={{ base: 'gray.200', md: 'transparent' }}
							zIndex={4}
							position="absolute"
							top={{ base: -3, md: -0.5 }}
							right={{ base: 4, md: 0 }}
							borderRadius="full"
						/>
						{props.InnerEvent ? (
							<props.InnerEvent event={editedEvent} metadata={{ ...editedEvent }} />
						) : (
							<WeekCalendarEvent event={editedEvent} />
						)}
						<Box
							css={{ touchAction: 'none' }}
							position="absolute"
							visibility={{
								base: 'visible',
								md: 'visible',
							}}
							h={{ base: 6, md: 2 }}
							w={{ base: 6, md: 'full' }}
							bottom={{ base: -3, md: -0.5 }}
							left={{ base: 4, md: 0 }}
							borderRadius="full"
							className="resize-handle"
							onPointerDown={(e) => handleEventResizePointerDown(e, editedEvent, 'end')}
							onPointerUp={() => handleEventResizePointerUp()}
							cursor="row-resize"
							bg={{
								base: 'gray.200',
								md: 'transparent',
							}}
							zIndex={4}
						></Box>
					</Grid>
				) : null}
			</Box>
		</Box>
	);
}
